import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import tw from 'twin.macro';
import { faLightbulb, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PercentageSelect from '../PercentageSelect';
import { useMutation } from '@apollo/client';
import { GET_CAR, UPDATE_CAR_DISCOUNT } from '../../services/carService/queries';
import { IDiscount } from '../../../__generated__/graphql';
import { Button } from '../button';

const PricingSection = styled.div`
  ${tw`mb-8`}
`;

const SectionTitle = styled.h1`
  ${tw`text-2xl font-bold flex items-center gap-2 mb-4`}
`;

const InfoIcon = styled(FontAwesomeIcon)`
  ${tw`text-text-tertiary cursor-help`}
`;

const BoostPricingCard = styled.div`
  ${tw`border border-border-default rounded-lg p-6 mb-4`}
`;

const SubSectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-4`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-4`}
`;

const LinkButton = styled.button`
  ${tw`text-black font-semibold flex items-center gap-2 mb-6`}
  &:hover {
    ${tw`text-black`}
  }
`;

const TipCard = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-lg flex items-start gap-3`}
`;

const DurationDiscountSection = styled.div`
  ${tw`border border-border-default rounded-lg p-6 mb-4`}
`;

const SubTitle = styled.h3`
  ${tw`text-base font-semibold mb-2`}
`;

const DiscountDescription = styled.p`
  ${tw`text-text-secondary mb-6`}
`;

const InputGroup = styled.div`
  ${tw`
    mb-6 
    w-full 
    max-w-md
  `}
`;

const InputLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-1`}
`;

const RecommendedText = styled.p`
  ${tw`text-sm text-text-secondary mt-1`}
`;

const UpdatedBadge = styled.span`
  ${tw`ml-2 text-xs bg-blue-100 text-black px-2 py-0.5 rounded-full`}
`;

const SaveButtonContainer = styled.div`
  ${tw`
    max-w-md
    mt-6
    py-2
  `}
`;

interface DiscountSectionProps {
  discount: IDiscount;
  carId: string;
}

const DiscountComponent: React.FC<DiscountSectionProps> = ({ discount, carId }) => {
  const [lastMinBooth, setLastMinBooth] = useState(Number(discount?.lastMinBooth) || 110);
  const [threeDays, setThreeDays] = useState(discount?.threeDays || 10);
  const [oneWeek, setOneWeek] = useState(discount?.oneWeek || 15);
  const [twoWeeks, setTwoWeeks] = useState(discount?.twoWeeks || 20);
  const [threeWeeks, setThreeWeeks] = useState(discount?.threeWeeks || 25);
  const [oneMonth, setOneMonth] = useState(discount?.oneMonth || 30);
  const [twoMonths, setTwoMonths] = useState(discount?.twoMonths || 35);
  const [threeMonths, setThreeMonths] = useState(discount?.threeMonths || 40);

  // Add mutation hook
  const [updateDiscount, { loading }] = useMutation(UPDATE_CAR_DISCOUNT, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      // Handle success (e.g., show success message)
      toast.success('Discount updated successfully');
    },
    onError: error => {
      // Handle error (e.g., show error message)
      toast.error('Error updating discount');
    },
  });

  const hasInvalidDiscounts = () => {
    return (
      threeDays > oneWeek ||
      oneWeek < threeDays ||
      oneWeek > twoWeeks ||
      twoWeeks < oneWeek ||
      twoWeeks > threeWeeks ||
      threeWeeks < twoWeeks ||
      threeWeeks > oneMonth ||
      oneMonth < threeWeeks ||
      oneMonth > twoMonths ||
      twoMonths < oneMonth ||
      twoMonths > threeMonths ||
      threeMonths < twoMonths
    );
  };

  const handleSave = async () => {
    if (!hasInvalidDiscounts()) {
      try {
        await updateDiscount({
          variables: {
            carId,
            discountData: {
              lastMinBooth,
              threeDays,
              oneWeek,
              twoWeeks,
              threeWeeks,
              oneMonth,
              twoMonths,
              threeMonths,
            },
          },
        });
      } catch (error) {}
    }
  };

  const handleSetLastMinBooth = (val: number) => {
    setLastMinBooth(val + 100);
  };

  return (
    <PricingSection>
      <SectionTitle>
        Pricing & discounts
        <InfoIcon icon={faInfoCircle} />
      </SectionTitle>

      <BoostPricingCard>
        <SubSectionTitle>Last-minute boost pricing</SubSectionTitle>
        <Description>
          Earn more on last-minute trips by automatically boosting your prices for same-day
          bookings.
        </Description>
        <Description>
          Make sure your vehicle's advance notice settings allow for same-day bookings in your Trip
          preferences.
        </Description>
        <LinkButton>How does boost pricing work?</LinkButton>

        <InputGroup>
          <InputLabel>Price boost</InputLabel>
          <PercentageSelect
            value={Number(lastMinBooth) - 100}
            onChange={handleSetLastMinBooth}
            minValue={0}
            maxValue={50}
          />
          <RecommendedText>Recommended: 10%</RecommendedText>
        </InputGroup>

        <TipCard>
          <FontAwesomeIcon icon={faLightbulb} tw="text-black mt-1" />
          <p>Guests searching at the last-minute are 2x more likely to book.</p>
        </TipCard>
      </BoostPricingCard>

      <DurationDiscountSection>
        <SubSectionTitle>
          Duration discount
          <UpdatedBadge>Updated</UpdatedBadge>
        </SubSectionTitle>

        <DiscountDescription>
          Encourage guests to book longer trips by offering discounts for weekend, weekly, and
          monthly trips.
        </DiscountDescription>

        <div>
          <SubTitle>Days & weeks</SubTitle>

          <InputGroup>
            <InputLabel>3-day</InputLabel>
            <PercentageSelect
              value={threeDays}
              onChange={setThreeDays}
              minValue={0}
              maxValue={50}
              isInvalid={threeDays > oneWeek}
              errorMessage={
                threeDays > oneWeek ? '3-day discount cannot be higher than 1-week discount' : ''
              }
            />
            <RecommendedText>Recommended: 10%</RecommendedText>
          </InputGroup>

          <InputGroup>
            <InputLabel>1-week</InputLabel>
            <PercentageSelect
              value={oneWeek}
              onChange={setOneWeek}
              minValue={0}
              maxValue={60}
              isInvalid={oneWeek < threeDays || oneWeek > twoWeeks}
              errorMessage={
                oneWeek < threeDays
                  ? '1-week discount must be higher than 3-day discount'
                  : oneWeek > twoWeeks
                    ? '1-week discount cannot be higher than 2-week discount'
                    : ''
              }
            />
            <RecommendedText>Recommended: 15%</RecommendedText>
          </InputGroup>

          <InputGroup>
            <InputLabel>2-week</InputLabel>
            <PercentageSelect
              value={twoWeeks}
              onChange={setTwoWeeks}
              minValue={0}
              maxValue={70}
              isInvalid={twoWeeks < oneWeek || twoWeeks > threeWeeks}
              errorMessage={
                twoWeeks < oneWeek
                  ? '2-week discount must be higher than 1-week discount'
                  : twoWeeks > threeWeeks
                    ? '2-week discount cannot be higher than 3-week discount'
                    : ''
              }
            />
            <RecommendedText>Recommended: 20%</RecommendedText>
          </InputGroup>

          <InputGroup>
            <InputLabel>3-week</InputLabel>
            <PercentageSelect
              value={threeWeeks}
              onChange={setThreeWeeks}
              minValue={0}
              maxValue={80}
              isInvalid={threeWeeks < twoWeeks || threeWeeks > oneMonth}
              errorMessage={
                threeWeeks < twoWeeks ? '3-week discount must be higher than 2-week discount' : ''
              }
            />
            <RecommendedText>Recommended: 25%</RecommendedText>
          </InputGroup>
        </div>

        <div>
          <SubTitle>Months</SubTitle>
          <TipCard tw="mb-4">
            <FontAwesomeIcon icon={faInfoCircle} tw="text-black mt-1" />
            <p>
              The <strong>percentage</strong> values below apply to DAILY bookings. Fixed monthly
              tier prices (30 / 60 / 90 day totals) are configured under{' '}
              <strong>Monthly pricing</strong> in the listing sidebar.
            </p>
          </TipCard>

          <InputGroup>
            <InputLabel>1-month percentage discount</InputLabel>
            <PercentageSelect
              value={oneMonth}
              onChange={setOneMonth}
              minValue={0}
              maxValue={90}
              isInvalid={oneMonth < threeWeeks || oneMonth > twoMonths}
              errorMessage={
                oneMonth < threeWeeks ? '1-month discount must be higher than 3-week discount' : ''
              }
            />
            <RecommendedText>Recommended: 30%</RecommendedText>
          </InputGroup>

          <InputGroup>
            <InputLabel>2-month percentage discount</InputLabel>
            <PercentageSelect
              value={twoMonths}
              onChange={setTwoMonths}
              minValue={0}
              maxValue={95}
              isInvalid={twoMonths < oneMonth || twoMonths > threeMonths}
              errorMessage={
                twoMonths < oneMonth ? '2-month discount must be higher than 1-month discount' : ''
              }
            />
            <RecommendedText>Recommended: 35%</RecommendedText>
          </InputGroup>

          <InputGroup>
            <InputLabel>3-month percentage discount</InputLabel>
            <PercentageSelect
              value={threeMonths}
              onChange={setThreeMonths}
              minValue={0}
              maxValue={99}
              isInvalid={threeMonths < twoMonths}
              errorMessage={
                threeMonths < twoMonths
                  ? '3-month discount must be higher than 2-month discount'
                  : ''
              }
            />
            <RecommendedText>Recommended: 40%</RecommendedText>
          </InputGroup>
        </div>

        <SaveButtonContainer>
          <Button
            text="Save"
            onClick={handleSave}
            isDisabled={hasInvalidDiscounts() || loading}
            isLoading={loading}
          />
        </SaveButtonContainer>
      </DurationDiscountSection>
    </PricingSection>
  );
};

export default DiscountComponent;
